<template>
  <Card class="mt-4 px-2" pt:content:class="p-0">
    <template #header>
      <div v-if="messages.length" class="flex flex-col gap-2">
        <Message severity="error" closable>
          <div class="flex flex-col gap-1">
            <p v-for="message in messages">{{ message }}</p>
          </div>
        </Message>
      </div>
    </template>
    <template #content>
      <Stepper :value="active" linear>
        <StepList>
          <Step v-slot="{ value, activateCallback = () => {} }" :value="1">
            <button class="bg-transparent border-none inline-flex flex-column gap-2 text-primary-500" type="button" @click="activateCallback">
              <span
                :class="[
                  'rounded-md border-2 w-[3rem] h-[3rem] inline-flex items-center justify-center',
                  {
                    'bg-primaryborder-primary*500 dark:border-primary-400': (typeof value === 'number' ? value : parseInt(value)) <= active,
                    'border-surface-200 dark:border-surface-700': (typeof value === 'number' ? value : parseInt(value)) > active,
                  },
                ]"
              >
                <i class="pi pi-user text-2xl"></i>
              </span>
            </button>
          </Step>
          <Step v-slot="{ value, activateCallback = () => {} }" :value="2">
            <button class="bg-transparent border-none inline-flex flex-column gap-2 text-primary-500" type="button" @click="activateCallback">
              <span
                :class="[
                  'rounded-md border-2 w-[3rem] h-[3rem] inline-flex items-center justify-center',
                  {
                    'bg-primaryborder-primary*500 dark:border-primary-400': (typeof value === 'number' ? value : parseInt(value)) <= active,
                    'border-surface-200 dark:border-surface-700': (typeof value === 'number' ? value : parseInt(value)) > active,
                  },
                ]"
              >
                <i class="pi pi-address-book text-2xl"></i>
              </span>
            </button>
          </Step>
          <Step v-slot="{ value, activateCallback = () => {} }" :value="3">
            <button class="bg-transparent border-none inline-flex flex-column gap-2 text-primary-500" type="button" @click="activateCallback">
              <span
                :class="[
                  'rounded-md border-2 w-[3rem] h-[3rem] inline-flex items-center justify-center',
                  {
                    'bg-primaryborder-primary*500 dark:border-primary-400': (typeof value === 'number' ? value : parseInt(value)) <= active,
                    'border-surface-200 dark:border-surface-700': (typeof value === 'number' ? value : parseInt(value)) > active,
                  },
                ]"
              >
                <i class="pi pi-building-columns text-2xl"></i>
              </span>
            </button>
          </Step>
          <Step v-slot="{ value, activateCallback = () => {} }" :value="4">
            <button class="bg-transparent border-none inline-flex flex-column gap-2 text-primary-500" type="button" @click="activateCallback">
              <span
                :class="[
                  'rounded-md border-2 w-[3rem] h-[3rem] inline-flex items-center justify-center',
                  {
                    'bg-primaryborder-primary*500 dark:border-primary-400': (typeof value === 'number' ? value : parseInt(value)) <= active,
                    'border-surface-200 dark:border-surface-700': (typeof value === 'number' ? value : parseInt(value)) > active,
                  },
                ]"
              >
                <i class="pi pi-cog text-2xl"></i>
              </span>
            </button>
          </Step>
        </StepList>
        <StepPanels>
          <StepPanel v-slot="{ value }" :value="1">
            <EntityInfoStep
              :entity="props.entity"
              :mode="props.mode"
              :data="currentValues"
              :setValues="formTracking.get(value)"
              @submit-step="nextStep"
            >
              <template #navigation>
                <div class="flex justify-end mt-3">
                  <NextButton type="submit" />
                </div>
              </template>
            </EntityInfoStep>
          </StepPanel>
          <StepPanel v-slot="{ value }" :value="2">
            <EntityContactStep
              :entity="props.entity"
              :mode="props.mode"
              :data="currentValues"
              :setValues="formTracking.get(value)"
              @submit-step="nextStep"
            >
              <template #navigation>
                <div class="flex justify-between mt-3">
                  <BackButton @click="previousStep" />
                  <NextButton type="submit" />
                </div>
              </template>
            </EntityContactStep>
          </StepPanel>
          <StepPanel v-slot="{ value }" :value="3">
            <EntityPaymentStep
              ref="entityPaymentStep"
              :entity="props.entity"
              :mode="props.mode"
              :data="currentValues"
              :setValues="formTracking.get(value)"
              @submit-step="nextStep"
            >
              <template #navigation>
                <div class="flex justify-between mt-3">
                  <BackButton @click="previousStep" />
                  <NextButton type="submit" />
                </div> </template
            ></EntityPaymentStep>
          </StepPanel>
          <StepPanel v-slot="{ value }" :value="4">
            <EntityOptionsStep
              :entity="props.entity"
              :mode="props.mode"
              :data="currentValues"
              :setValues="formTracking.get(value)"
              @submit-step="nextStep"
            >
              <template #navigation>
                <div class="flex justify-between mt-3">
                  <BackButton @click="previousStep" />
                  <SubmitButton type="submit" /></div
              ></template>
            </EntityOptionsStep>
          </StepPanel>
        </StepPanels>
      </Stepper>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { EntitySchema, type Entity } from "@/types/Entity";
import { useConfirm } from "primevue/useconfirm";
import { ErrorCodes } from "@/enums/ErrorCodes";
import EntityPaymentStep from "./EntityPaymentStep.vue";

const formCount = 4;
const formTracking = new Map();

for (let i = 0; i < formCount; i++) {
  formTracking.set(i, false);
}

const active = ref<number>(1);
const confirm = useConfirm();
const messages = ref<string[]>([]);

const emit = defineEmits<{
  submitted: [entity: Entity | null];
}>();

interface Props {
  entity?: Entity;
  mode: "Add" | "Edit" | "Copy";
}
const props = defineProps<Props>();

const currentValues: any = {};

if (props.entity) {
  if (props.mode === "Edit") {
    currentValues.firstName = props.entity.firstName;
    currentValues.lastName = props.entity.lastName;
    currentValues.companyName = props.entity.companyName;
  } else {
    currentValues.firstName = "";
    currentValues.lastName = "";
    currentValues.companyName = "";
  }

  currentValues.extension = props.entity.extension;
  currentValues.phone = props.entity.entityPhone;
  currentValues.fax = props.entity.fax;
  currentValues.email = props.entity.email;
  currentValues.fedTaxId = "";
  currentValues.stateTaxId = "";
  currentValues.companyPhone = props.entity.companyPhone;
  currentValues.addressLine1 = props.entity.addressLine1;
  currentValues.addressLine2 = props.entity.addressLine2;
  currentValues.city = props.entity.city;
  currentValues.state = props.entity.state == null ? "" : props.entity.state;
  currentValues.zip = props.entity.zip;
  currentValues.payeeName = props.entity.payee;
  currentValues.bankNumber = props.entity.bankNumber;
  currentValues.bankAccount = props.entity.bankAccount;
  currentValues.bankName = props.entity.bankName;
  currentValues.bankCity = props.entity.bankCity;
  currentValues.bankABANumber = props.entity.bankABANumber;
}

const previousStep = (): void => {
  if (active.value > 0) {
    active.value--;
  }
};

const entityPaymentStep = ref();
const nextStep = (values: any): void => {
  Object.assign(currentValues, values);
  formTracking.set(active.value, true);

  if (active.value < formCount) {
    active.value++;
    if (active.value == 3 && currentValues.payeeName == null) {
      entityPaymentStep.value?.defaultPayee();
    }
    return;
  }

  completeEntityWizard(true);
};

const completeEntityWizard = (checkSimilar: boolean): void => {
  let url = props.mode === "Edit" ? "/entities/edit/" + props.entity!.entityId : "/entities/add";

  url += "?checkSimilar=" + checkSimilar;

  axios
    .post(url, currentValues)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          if (props.mode === "Edit") {
            success("Success", "Entity edited successfully");
          } else if (props.mode === "Add") {
            success("Success", "Entity added successfully");
          } else {
            success("Success", "Entity copied successfully");
          }

          let entity = null;
          if (props.mode === "Add") entity = EntitySchema.parse(response.data.result);
          emit("submitted", entity);
        },
        (errors: ApiError[]) => {
          while (messages.value.length) {
            messages.value.pop();
          }

          active.value = 1;

          let confirmSimilar = false;

          for (let error of errors) {
            if (error.code === ErrorCodes.SIMILAR_ENTITIES_EXIST) {
              confirmSimilar = true;
            } else {
              messages.value.push(error.message);
            }
          }

          if (messages.value.length) {
            messages.value.unshift("Please fix the following errors:");
          } else if (confirmSimilar) {
            confirmPopup(
              confirm,
              "Similar Entity Found",
              "A similar entity exists, are you sure you want to continue?",
              () => completeEntityWizard(false),
              () => info("Action cancelled", "Entity not added")
            );
          } else {
            completeEntityWizard(false);
          }
        }
      )
    )
    .catch((e) => errorApi(e));
};
</script>
